import {
    Component,
    type OnInit,
    ChangeDetectionStrategy,
    Input,
    ViewChild,
    type Type,
    type ComponentRef,
    inject,
    booleanAttribute,
} from '@angular/core';
import { of } from 'rxjs';

import { SpHostDirective } from '@common/directives/host.directive';
import { InputBoolean } from '@common/helpers/convert';

import type { WebLegacyWidgetEntity, WebWidgetModel } from '@web/types';
import { type WebWidgetViewComponent } from '@web/types';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { widgetNameOf } from '@common/helpers/widget-envelope';

@Component({
    selector: 'web-view-resolver',
    template: '<ng-container spHost></ng-container>',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpHostDirective],
})
export class ViewResolverComponent implements OnInit {
    @Input() public widget: WebLegacyWidgetEntity;
    @Input({ transform: booleanAttribute }) @InputBoolean() public isSpecific: boolean = false;

    @ViewChild(SpHostDirective, { static: true }) public widgetHost: SpHostDirective;

    private readonly widgetsService = inject(WebWidgetsService);
    private readonly widgetsFacade = inject(WidgetsFacade);

    public ngOnInit(): void {
        this.loadComponent();
    }

    private loadComponent(): void {
        const component: Type<WebWidgetViewComponent> = this.widgetsService.getWidgetViewComponent(
            widgetNameOf(this.widget)
        );

        if (!component) {
            return;
        }

        const model: WebWidgetModel = this.widgetsService.getWidgetModel(widgetNameOf(this.widget));

        if (model?.signalForm) {
            const { viewContainerRef } = this.widgetHost;
            viewContainerRef.clear();

            const componentRef: ComponentRef<WebWidgetViewComponent> = viewContainerRef.createComponent(
                component as Type<WebWidgetViewComponent>
            );

            componentRef.setInput('widget', this.widget);
            componentRef.setInput('isSpecific', this.isSpecific);
            return;
        }

        const { viewContainerRef } = this.widgetHost;
        viewContainerRef.clear();

        const componentRef: ComponentRef<WebWidgetViewComponent> = viewContainerRef.createComponent(
            component as Type<WebWidgetViewComponent>
        );

        const instance = componentRef.instance as any;
        instance.container$ = of({});
        instance.widget$ = this.widgetsFacade.widgetById$(this.widget.id);
        instance.isSpecific = this.isSpecific;
    }
}
